import { AfterViewInit, Component, EventEmitter, Inject, OnInit, Output, ViewChild } from "@angular/core";
import { CardDetailAddComponent, CardDetailAddData } from "@app/components/card/card-detail-add.component";
import {
    AddressFormComponentProps,
    AddressFormData,
    CardAddressComponent
} from "@app/components/card/card-address.component";
import { Def } from "@card/common/definitions";
import { take } from "rxjs/operators";
import { UserStore } from "@app/services/stores/UserStore";
import { SpinnerService } from "@app/services/spinner.service";
import { ErrorHandlerService } from "@app/services/ErrorHandler.service";
import { PaymentMethodApi } from "@app/services/apiServices/PaymentMethodApi";
import { PaymentSchedule } from "@app/models/PaymentSchedule";
import { MatDialog } from "@angular/material/dialog";
import { DetailsPropagateDefaultDialog } from "@app/pages/wallet/dialogs/details-propagate-default.dialog";
import { PaymentDeclinedDialog } from "@app/components/sharedDialogs/payment-declined.dialog";
import { DialogErrorDialog } from "@app/components/sharedDialogs/dialogError.dialog";
import { NetworkErrorService } from "@app/services/NetworkError.service";

@Component({
    selector: 'app-credit-card-checkout-add',
    template: require('./credit-card-add.component.html')
})
export class CreditCardCheckoutAddComponent implements OnInit, AfterViewInit {

    @Output() onReady = new EventEmitter<void>();
    @Output() onReturn = new EventEmitter<void>();

    @ViewChild(CardDetailAddComponent) cardDetailAddComponent: CardDetailAddComponent;
    @ViewChild(CardAddressComponent) cardAddressComponent: CardAddressComponent;

    private paymentSchedules: PaymentSchedule[];
    private UNSELECTED = -1
    private cardFormValid: boolean = false;
    private addressFormValid: boolean = false;
    private prefillAddressForm = this.UNSELECTED;
    private addressData: AddressFormData;
    private cardFormData: CardDetailAddData;
    private cardProps = {}

    addressProps: AddressFormComponentProps = {
        disablePOBoxes: false
    }

    constructor(@Inject(SpinnerService) private spinnerService: SpinnerService,
                @Inject(PaymentMethodApi) private paymentMethodApi: PaymentMethodApi,
                @Inject(ErrorHandlerService) private errorHandlerService: ErrorHandlerService,
                @Inject(NetworkErrorService) private networkErrorService: NetworkErrorService,
                @Inject(MatDialog) private dialog: MatDialog,
                @Inject(UserStore) private userStore: UserStore) {

        this.paymentMethodApi.getDefaultPaymentMethodSchedules(this.userStore.getHoaHomeownerId())
            .pipe(take(1)).subscribe(resp => this.paymentSchedules = resp);
    }

    ngOnInit(): void {
    }

    onCancel() {
        this.onReturn.emit();
    }

    ngAfterViewInit(): void {
        this.onReady.emit();
    }

    preSubmit() {
        this.addressData = this.cardAddressComponent.getFormData();
        this.cardFormData = this.cardDetailAddComponent.getFormData();

        if (this.cardFormData.makeDefault && this.paymentSchedules && this.paymentSchedules.length > 0) {
            const dialogRef = this.dialog.open(DetailsPropagateDefaultDialog, {
                closeOnNavigation: false,
                disableClose: true,
                data: { paymentSchedules: this.paymentSchedules, type: 'Add' }
            });

            dialogRef.afterClosed().pipe(take(1)).subscribe({
                next: dialogData => {
                    if (dialogData === 'Save') {
                        this.onSubmit(false);
                    } else if (dialogData === 'Update') {
                        this.onSubmit(true);
                    }
                }
            })
        } else {
            this.onSubmit(false);
        }

    }

    onSubmit(propagatePmtMethod: boolean) {
        this.spinnerService.showSpinner();

        const payerId = this.userStore.getHoaHomeownerId();

        this.cardDetailAddComponent.createCard(payerId, this.addressData, this.cardFormData).then(cardUpdate => {
            const error = cardUpdate as Def.CardError
            const hasError = error.ftni !== undefined;
            if (hasError && error.ftni.FTNIApprovalCode) {
                const dialogRef = this.dialog.open(PaymentDeclinedDialog, {
                    data: { errorMessage: "We are unable to add this card to your wallet because it was declined. Please contact the bank that issued your card for more information." }
                })
                dialogRef.afterClosed().pipe(take(1)).subscribe({
                    next: () => {
                    },
                    error: () => {
                        this.spinnerService.closeSpinner();
                        this.onReturn.emit();
                    }
                });
            } else {
                this.paymentMethodApi.paymentMethodsPost({
                    payerId: payerId,
                    token: String(cardUpdate.paymentToken),
                    cardType: "DEBIT", // todo: figure this out
                    // ^-- Chris B said not an issue "for now" - we cant determine card type anyways apparently
                    cardBrand: (cardUpdate.cardBrand || '').toLowerCase() || undefined, // 'amex', 'discover', 'mastercard', 'visa'
                    maskedId: cardUpdate.maskedCardNumber.slice(-4),

                    cardHolderName: cardUpdate.cardHolderName,

                    nickname: this.cardFormData.nickName,
                    defaultMethod: this.cardFormData.makeDefault,

                    expirationYear: cardUpdate.expirationYear,
                    expirationMonth: cardUpdate.expirationMonth,

                    line1: this.addressData.address1,
                    line2: this.addressData.address2,
                    unit: this.addressData.unit,
                    city: this.addressData.city,
                    state: this.addressData.state,
                    zipcode: this.addressData.zip,
                    country: this.addressData.country,
                }).pipe(take(1)).subscribe({
                    next: async resp => {
                        if (propagatePmtMethod && this.paymentSchedules && this.paymentSchedules.length) {
                            await this.paymentMethodApi.paymentMethodPropagateToSchedules(this.paymentSchedules, resp,
                                this.paymentSchedules[0].paymentMethod.type !== 'card').then(resp => {
                                if (resp && resp.length > 0) {
                                    this.dialog.open(DialogErrorDialog, {
                                        data: {
                                            message: 'An error occurred when attempting to save or update Payment Schedules. ' +
                                                    'Please verify that the schedules: ' + resp.join(',') + ' were processed correctly. ' +
                                                    'If not, you may need to change the payment method manually.'
                                        }
                                    })
                                }
                            },
                            () => {
                                this.dialog.open(DialogErrorDialog, {
                                    data: {
                                        message: 'An error occurred when attempting to save or update Payment Schedules. ' +
                                                'Please verify that the schedules were processed correctly. If not, you may need to ' +
                                                'change the payment method manually.'
                                    }
                                })
                            });
                        }
                        this.spinnerService.closeSpinner();
                        this.onReturn.emit();
                    },
                    error: (err) => {
                        if (err && err.status === 400) {
                            const dialogRef = this.dialog.open(PaymentDeclinedDialog, {
                                data: {
                                    errorMessage: "We are unable to add this card to your wallet because it was declined. " +
                                        "Please contact the bank that issued your card for more information."
                                }
                            })

                            dialogRef.afterClosed().pipe(take(1)).subscribe({
                                next: () => {
                                    this.spinnerService.closeSpinner();
                                    this.onReturn.emit()
                                },
                                error: () => {
                                }
                            })

                        } else {
                            this.spinnerService.closeSpinner();
                            this.networkErrorService.handleNetworkError(err, true)
                                .pipe(take(1)).subscribe((resp) => {
                                    if (resp === true) {
                                        this.onSubmit(propagatePmtMethod);
                                    }
                                });
                        }
                    }
                });
            }
        }).catch (() => {
            this.spinnerService.closeSpinner();
            let errMsg: string = 'System error occurred. Please try again in a few hours.';
            let err = {
                status: 400
            }
            this.errorHandlerService.handleNetworkError(err, true, errMsg)
                .pipe(take(1)).subscribe();
        })
    }

    onAddressValid(isValid: boolean) {
        this.addressFormValid = isValid;
    }

    onCardValid(isValid: boolean) {
        this.cardFormValid = isValid;
    }

}
